import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { todayISO } from "@/lib/utils";

interface Person {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  job_title: string | null;
  department: string | null;
  profile_picture_path: string | null;
  has_pin: boolean;
}

export function Kiosk() {
  const { signOut } = useAuth();
  const nav = useNavigate();
  const [people, setPeople] = useState<Person[]>([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Person | null>(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ action: string; name: string } | null>(null);
  const [now, setNow] = useState(new Date());
  const [pinResetSent, setPinResetSent] = useState(false);
  const [pinResetBusy, setPinResetBusy] = useState(false);

  // Webcam state
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraStatus, setCameraStatus] = useState<"requesting" | "ready" | "denied" | "unavailable">("requesting");

  useEffect(() => { void load(); void initCamera(); return () => stopCamera(); }, []);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  async function load() {
    const { data } = await supabase.from("hq_kiosk_directory").select("*").order("first_name");
    setPeople((data as Person[]) ?? []);
  }

  async function initCamera() {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraStatus("unavailable");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
        audio: false,
      });
      streamRef.current = stream;
      setCameraStatus("ready");
    } catch (e) {
      setCameraStatus("denied");
    }
  }

  function stopCamera() {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  }

  function attachVideo(el: HTMLVideoElement | null) {
    videoRef.current = el;
    if (el && streamRef.current) {
      el.srcObject = streamRef.current;
      void el.play().catch(() => {});
    }
  }

  async function capturePhoto(): Promise<Blob | null> {
    const v = videoRef.current;
    if (!v || !streamRef.current) return null;
    if (cameraStatus !== "ready") return null;
    // Wait up to 3 seconds for the video element to actually have dimensions.
    // Camera permission can be granted while the stream is still buffering.
    const deadline = Date.now() + 3000;
    while ((v.videoWidth === 0 || v.videoHeight === 0) && Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 100));
    }
    if (v.videoWidth === 0 || v.videoHeight === 0) {
      console.warn("[kiosk] video has no dimensions after 3s wait", { rs: v.readyState, ns: v.networkState });
      return null;
    }
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    return await new Promise<Blob | null>(res => canvas.toBlob(b => res(b), "image/jpeg", 0.75));
  }

  function tap(p: Person) {
    setSelected(p);
    setPin("");
    setError(null);
  }

  async function submit() {
    if (!selected || pin.length < 4) return;
    setBusy(true); setError(null);

    // Capture photo BEFORE the PIN check so we have a frame even if PIN is wrong.
    const blob = await capturePhoto();

    let photoPath: string | null = null;
    let photoFailure: string | null = null;
    if (!blob) {
      photoFailure = cameraStatus === "denied" ? "Camera permission denied"
        : cameraStatus === "unavailable" ? "No camera available on this device"
        : "Camera not ready — photo skipped";
      console.warn("[kiosk] photo capture failed:", photoFailure);
    } else {
      const ymd = todayISO();
      const filename = `${selected.id}-${Date.now()}.jpg`;
      const path = `${ymd}/${filename}`;
      const { error: upErr } = await supabase.storage
        .from("kiosk-photos")
        .upload(path, blob, { contentType: "image/jpeg", upsert: false });
      if (upErr) {
        photoFailure = "Upload failed: " + upErr.message;
        console.warn("[kiosk] photo upload failed:", upErr);
      } else {
        photoPath = path;
      }
    }

    // 2. Validate PIN + clock action
    const { data, error } = await supabase.rpc("hq_kiosk_clock", {
      p_employee_id: selected.id,
      p_pin: pin,
      p_photo_path: photoPath,
    });
    setBusy(false);

    if (error) {
      // Reset the "forgot PIN" UI state when modal re-opens
      // If PIN was wrong, the photo upload was wasted but harmless — it'll get
      // garbage-collected via storage retention policies.
      setError(error.message.replace(/^.*: /, ""));
      setPin("");
      return;
    }
    // A wrong PIN or a lockout comes back as { error } rather than a raised
    // exception: raising would roll back the failed-attempt record, and the
    // lockout counts those records.
    const refused = data as { error?: string } | null;
    if (refused?.error) {
      setError(refused.error);
      setPin("");
      return;
    }
    const result = data as { action: string; first_name: string };
    setSuccess({ action: result.action, name: result.first_name || selected.first_name || "There" });
    // Log photo failures to the time log notes so admin can see what happened
    if (photoFailure && result) {
      try {
        const { data: latest } = await supabase
          .from("hq_time_logs")
          .select("id,notes")
          .eq("employee_id", selected.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (latest) {
          const noteRow = latest as { id: string; notes: string | null };
          const note = `[no photo] ${photoFailure}`;
          await supabase.from("hq_time_logs").update({
            notes: noteRow.notes ? `${noteRow.notes} · ${note}` : note,
            verification_status: "unverified",
          }).eq("id", noteRow.id);
        }
      } catch { /* ignore */ }
    }
    setSelected(null);
    setPin("");
    void load();
    setTimeout(() => setSuccess(null), 4000);
  }

  useEffect(() => { if (!selected) { setPinResetSent(false); setPinResetBusy(false); } }, [selected]);
  function backspace() { setPin(p => p.slice(0, -1)); setError(null); }
  function press(d: string) {
    if (pin.length >= 6) return;
    setPin(p => p + d);
    setError(null);
  }

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return people;
    return people.filter(p => `${p.first_name ?? ""} ${p.last_name ?? ""} ${p.email}`.toLowerCase().includes(q));
  }, [people, search]);

  async function exitKiosk() {
    if (!confirm("Exit kiosk mode and sign out?")) return;
    stopCamera();
    await signOut();
    nav("/login");
  }

  useEffect(() => {
    if (selected && pin.length === 4) void submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-offwhite via-white to-teal-mint/40">
      <div className="flex items-center justify-between border-b border-line bg-white px-6 py-4">
        <div className="flex items-center gap-3">
          <img src="/assets/logo.png" alt="Sunday State" className="h-11 w-auto" />
          <div>
            <div className="text-xl font-semibold text-teal-dark">HQ · Kiosk</div>
            <div className="text-xs text-gray2">Tap your name, enter your PIN to clock in or out.</div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-2xl font-bold text-ink">
            {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </div>
          <div className="text-xs text-gray2">
            {now.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <CameraIndicator status={cameraStatus} onRetry={initCamera} />
          <button onClick={exitKiosk} className="text-xs text-gray2 hover:text-bad">Exit kiosk</button>
        </div>
      </div>

      {success && (
        <div className="bg-ok-bg px-6 py-4 text-center text-xl font-semibold text-ok">
          ✓ {success.name} — {success.action === "clocked_in" ? "Clocked in. Have a great shift!" : "Clocked out. See you next time!"}
        </div>
      )}

      <div className="mx-auto w-full max-w-7xl flex-1 px-6 py-8">
        <input
          type="text"
          autoFocus
          placeholder="Type to find your name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="mb-6 w-full rounded-xl border-0 bg-white px-5 py-4 text-xl shadow-sm ring-1 ring-line focus:outline-none focus:ring-2 focus:ring-teal"
        />

        {filtered.length === 0 ? (
          <div className="rounded-xl bg-white p-12 text-center text-gray2 ring-1 ring-line">No employees found.</div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {filtered.map(p => (
              <button
                key={p.id}
                onClick={() => tap(p)}
                disabled={!p.has_pin}
                className="group flex flex-col items-center gap-3 rounded-xl bg-white p-5 shadow-sm ring-1 ring-line transition hover:-translate-y-1 hover:shadow-soft hover:ring-teal disabled:cursor-not-allowed disabled:opacity-50"
              >
                <EmployeeAvatar
                  path={p.profile_picture_path}
                  firstName={p.first_name}
                  lastName={p.last_name}
                  email={p.email}
                  size={80}
                  className="transition group-hover:ring-2 group-hover:ring-teal"
                />
                <div className="text-center">
                  <div className="font-semibold">{p.first_name || p.email}</div>
                  {p.last_name && <div className="text-xs text-gray2">{p.last_name}</div>}
                  {!p.has_pin && <div className="mt-1 text-[10px] uppercase tracking-wider text-warn">No PIN set</div>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* PIN modal with camera preview */}
      {selected && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/60 p-4" onClick={() => { setSelected(null); setPin(""); }}>
          <div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-soft"
            onClick={e => e.stopPropagation()}
          >
            {/* Camera preview */}
            <div className="mb-4 overflow-hidden rounded-xl bg-ink/90 ring-1 ring-line aspect-[4/3] relative">
              {cameraStatus === "ready" ? (
                <video
                  ref={attachVideo}
                  playsInline
                  muted
                  autoPlay
                  className="h-full w-full object-cover scale-x-[-1]"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-center text-white">
                  <div>
                    <div className="text-4xl">📷</div>
                    <div className="mt-2 text-sm">
                      {cameraStatus === "denied" ? "Camera access denied" :
                       cameraStatus === "unavailable" ? "Camera not available" :
                       "Starting camera…"}
                    </div>
                    {cameraStatus === "denied" && (
                      <button onClick={initCamera} className="mt-2 text-xs underline">Retry</button>
                    )}
                  </div>
                </div>
              )}
              <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between text-xs text-white/90">
                <span className="rounded bg-black/50 px-2 py-0.5 font-semibold">
                  {selected.first_name || selected.email}
                </span>
                {cameraStatus === "ready" && <span className="rounded bg-bad/80 px-2 py-0.5 font-semibold">● REC</span>}
              </div>
            </div>

            <div className="mb-3 text-center text-sm text-gray2">Enter your PIN</div>

            <div className="mb-3 flex items-center justify-center gap-3">
              {[0, 1, 2, 3, 4, 5].map(i => (
                <div
                  key={i}
                  className={`h-4 w-4 rounded-full transition ${
                    pin.length > i ? "bg-teal-dark scale-110" : i < 4 ? "bg-line" : "bg-line/40"
                  }`}
                />
              ))}
            </div>

            {error && (
              <div className="mb-3 rounded-lg bg-bad-bg px-3 py-2 text-center text-sm font-semibold text-bad">
                {error}
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                <Key key={n} onPress={() => press(String(n))}>{n}</Key>
              ))}
              <Key onPress={() => { setSelected(null); setPin(""); }} variant="ghost">Cancel</Key>
              <Key onPress={() => press("0")}>0</Key>
              <Key onPress={backspace} variant="ghost">⌫</Key>
            </div>

            <div className="mt-3 text-center">
              <button
                onClick={submit}
                disabled={busy || pin.length < 4}
                className="btn w-full"
              >
                {busy ? "…" : "Clock In / Out"}
              </button>
            </div>

            <div className="mt-3 text-center text-xs">
              {pinResetSent ? (
                <span className="text-ok">Admin notified — they'll reset your PIN.</span>
              ) : (
                <button
                  type="button"
                  disabled={pinResetBusy}
                  onClick={async () => {
                    if (!selected || pinResetBusy) return;
                    setPinResetBusy(true);
                    try {
                      await fetch("/.netlify/functions/pin-reset-request", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ employee_id: selected.id }),
                      });
                      setPinResetSent(true);
                    } finally {
                      setPinResetBusy(false);
                    }
                  }}
                  className="text-gray2 underline hover:text-charcoal"
                >
                  {pinResetBusy ? "Sending…" : "Forgot my PIN"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CameraIndicator({ status, onRetry }: { status: "requesting" | "ready" | "denied" | "unavailable"; onRetry: () => void }) {
  const map = {
    requesting: { dot: "bg-warn", label: "Starting camera…" },
    ready: { dot: "bg-ok", label: "Camera ready" },
    denied: { dot: "bg-bad", label: "Camera denied — click to retry" },
    unavailable: { dot: "bg-gray2", label: "No camera" },
  } as const;
  const s = map[status];
  return (
    <button
      onClick={status === "denied" ? onRetry : undefined}
      className="flex items-center gap-2 text-xs text-gray2"
      title={s.label}
    >
      <span className={`h-2 w-2 rounded-full ${s.dot}`} />
      <span>📷</span>
    </button>
  );
}

function Key({
  children,
  onPress,
  variant,
}: { children: React.ReactNode; onPress: () => void; variant?: "ghost" }) {
  return (
    <button
      onClick={onPress}
      className={`rounded-xl py-5 text-2xl font-semibold transition active:scale-95 ${
        variant === "ghost"
          ? "bg-offwhite text-charcoal hover:bg-line"
          : "bg-teal-mint text-teal-dark hover:bg-teal hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}
